// ─── WORKWAVE DATABASE HELPERS ───────────────────────────────────────────────
// Every function returns { data, error } so callers can handle failures cleanly.
// All queries respect Row Level Security — users only see their own rows.

import { supabase } from "./supabase";

// ── PROFILES ──────────────────────────────────────────────────────────────────

export async function getProfile(userId) {
  return supabase
    .from("profiles")
    .select("*")
    .eq("id", userId)
    .single();
}

export async function upsertProfile(userId, updates) {
  return supabase
    .from("profiles")
    .upsert({ id: userId, ...updates, updated_at: new Date().toISOString() })
    .select()
    .single();
}

// ── JOBS ──────────────────────────────────────────────────────────────────────

export async function getJobs({ search = "", remote = null, limit = 50 } = {}) {
  let query = supabase
    .from("jobs")
    .select("*")
    .eq("active", true)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (search) {
    query = query.or(
      `title.ilike.%${search}%,company.ilike.%${search}%,description.ilike.%${search}%`
    );
  }
  if (remote !== null) {
    query = query.eq("remote", remote);
  }
  return query;
}

export async function getJobById(id) {
  return supabase.from("jobs").select("*").eq("id", id).single();
}

// ── APPLICATIONS ──────────────────────────────────────────────────────────────

export async function getApplications(userId) {
  return supabase
    .from("applications")
    .select("*, jobs(*)")
    .eq("user_id", userId)
    .order("applied_at", { ascending: false });
}

export async function applyToJob(userId, jobId) {
  return supabase
    .from("applications")
    .upsert(
      { user_id: userId, job_id: jobId, stage: "Applied", applied_at: new Date().toISOString() },
      { onConflict: "user_id,job_id" }
    )
    .select()
    .single();
}

export async function updateApplicationStage(applicationId, stage) {
  return supabase
    .from("applications")
    .update({ stage, updated_at: new Date().toISOString() })
    .eq("id", applicationId)
    .select()
    .single();
}

// ── SAVED JOBS ────────────────────────────────────────────────────────────────

export async function getSavedJobs(userId) {
  return supabase
    .from("saved_jobs")
    .select("job_id")
    .eq("user_id", userId);
}

export async function saveJob(userId, jobId) {
  return supabase
    .from("saved_jobs")
    .upsert({ user_id: userId, job_id: jobId, saved_at: new Date().toISOString() })
    .select()
    .single();
}

export async function unsaveJob(userId, jobId) {
  return supabase
    .from("saved_jobs")
    .delete()
    .eq("user_id", userId)
    .eq("job_id", jobId);
}

// ── ALERTS ────────────────────────────────────────────────────────────────────

export async function getAlerts(userId) {
  return supabase
    .from("alerts")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
}

export async function createAlert(userId, alert) {
  return supabase
    .from("alerts")
    .insert({ user_id: userId, ...alert, created_at: new Date().toISOString() })
    .select()
    .single();
}

export async function updateAlert(alertId, updates) {
  return supabase
    .from("alerts")
    .update(updates)
    .eq("id", alertId)
    .select()
    .single();
}

export async function deleteAlert(alertId) {
  return supabase.from("alerts").delete().eq("id", alertId);
}

// ── MESSAGES ──────────────────────────────────────────────────────────────────

export async function getConversations(userId) {
  return supabase
    .from("messages")
    .select("*, sender:sender_id(id,full_name,avatar_url), recipient:recipient_id(id,full_name,avatar_url)")
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .order("sent_at", { ascending: false });
}

export async function sendMessage(senderId, recipientId, text) {
  return supabase
    .from("messages")
    .insert({
      sender_id: senderId,
      recipient_id: recipientId,
      text,
      sent_at: new Date().toISOString(),
      read: false,
    })
    .select()
    .single();
}

export async function markMessagesRead(userId, otherUserId) {
  return supabase
    .from("messages")
    .update({ read: true })
    .eq("recipient_id", userId)
    .eq("sender_id", otherUserId);
}

// ── REAL-TIME ─────────────────────────────────────────────────────────────────

// Subscribe to new messages for a user. Returns the channel (call .unsubscribe() on cleanup).
export function subscribeToMessages(userId, onMessage) {
  return supabase
    .channel(`messages:${userId}`)
    .on(
      "postgres_changes",
      { event: "INSERT", schema: "public", table: "messages", filter: `recipient_id=eq.${userId}` },
      payload => onMessage(payload.new)
    )
    .subscribe();
}
